/**
 * ScheduleImpactModal — Gap B do plano Keto Fusion.
 *
 * Aparece após o utilizador gravar um novo schedule semanal (se `changed=true`).
 * Mostra visualmente o que mudou:
 *   • dias adicionados ao treino
 *   • dias removidos
 *   • delta semanal de kcal, carbs, proteína
 *
 * Fecha automaticamente após "Entendi" ou clique fora.
 */

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, TrendingUp, TrendingDown, Plus, Minus, Dumbbell } from "lucide-react";
import { Button } from "@/components/ui/button";

const WEEKDAY_LABELS_PT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
function weekdayFromIso(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return WEEKDAY_LABELS_PT[d.getDay()];
  } catch {
    return iso;
  }
}
function shortDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return `${d.getDate()}/${d.getMonth() + 1}`;
  } catch {
    return iso;
  }
}
function formatDelta(n, unit = "") {
  if (!n || Math.abs(Math.round(n)) === 0) return `0${unit}`;
  const rounded = Math.round(n);
  return (rounded > 0 ? "+" : "") + rounded + unit;
}

export default function ScheduleImpactModal({ open, impact, onClose }) {
  if (!open || !impact) return null;

  const {
    added_days = [],
    removed_days = [],
    weekly_kcal_delta = 0,
    weekly_carbs_delta_g = 0,
    weekly_protein_delta_g = 0,
    training_days_new = 0,
    training_days_prev = 0,
  } = impact;

  const kcalUp = weekly_kcal_delta > 0;
  const carbsUp = weekly_carbs_delta_g > 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-end sm:items-center justify-center"
        onClick={onClose}
        data-testid="schedule-impact-modal"
      >
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 26, stiffness: 260 }}
          className="w-full sm:max-w-md relative rounded-t-3xl sm:rounded-3xl overflow-y-auto max-h-[92vh]"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "linear-gradient(180deg, rgba(132,204,22,0.10) 0%, #09090b 45%)",
            border: "1px solid rgba(132,204,22,0.35)",
            boxShadow: "0 -20px 60px rgba(132,204,22,0.25), inset 0 1px 0 rgba(132,204,22,0.3)",
          }}
        >
          <div className="sm:hidden flex justify-center pt-3 pb-1">
            <div className="w-12 h-1 rounded-full bg-zinc-700" />
          </div>

          <div className="px-6 pt-5 pb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-lime-400 flex items-center gap-1">
                  <Dumbbell className="w-3 h-3" /> Plano recalculado
                </p>
                <h3 className="text-2xl font-black text-zinc-100 leading-tight mt-0.5">
                  O teu plano mudou
                </h3>
                <p className="text-xs text-zinc-500 mt-1">
                  {training_days_prev} → {training_days_new} dias de treino esta semana
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-zinc-400 hover:text-zinc-100 p-1 -mt-1 -mr-1"
                data-testid="schedule-impact-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Days added / removed */}
            {(added_days.length > 0 || removed_days.length > 0) && (
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="p-3 rounded-xl border border-lime-500/30 bg-lime-500/5">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Plus className="w-3.5 h-3.5 text-lime-400" />
                    <span className="text-[10px] uppercase tracking-widest font-bold text-lime-400">
                      Adicionaste
                    </span>
                  </div>
                  {added_days.length === 0 && (
                    <p className="text-sm text-zinc-600 italic">nenhum</p>
                  )}
                  <div className="space-y-1">
                    {added_days.map((iso) => (
                      <p key={iso} className="text-sm font-bold text-zinc-100">
                        {weekdayFromIso(iso)} <span className="text-zinc-500 font-normal text-xs">· {shortDate(iso)}</span>
                      </p>
                    ))}
                  </div>
                </div>
                <div className="p-3 rounded-xl border border-orange-500/30 bg-orange-500/5">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Minus className="w-3.5 h-3.5 text-orange-400" />
                    <span className="text-[10px] uppercase tracking-widest font-bold text-orange-400">
                      Removeste
                    </span>
                  </div>
                  {removed_days.length === 0 && (
                    <p className="text-sm text-zinc-600 italic">nenhum</p>
                  )}
                  <div className="space-y-1">
                    {removed_days.map((iso) => (
                      <p key={iso} className="text-sm font-bold text-zinc-100">
                        {weekdayFromIso(iso)} <span className="text-zinc-500 font-normal text-xs">· {shortDate(iso)}</span>
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Weekly deltas */}
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Impacto semanal</p>
            <div className="space-y-2 mb-5">
              <div className="flex items-center justify-between p-3 rounded-xl bg-zinc-900/60 border border-zinc-800">
                <div className="flex items-center gap-2">
                  {kcalUp ? <TrendingUp className="w-4 h-4 text-emerald-400" /> : <TrendingDown className="w-4 h-4 text-amber-400" />}
                  <span className="text-sm font-semibold text-zinc-100">Calorias</span>
                </div>
                <span
                  className={`text-lg font-black tabular-nums ${kcalUp ? "text-emerald-400" : "text-amber-400"}`}
                  data-testid="impact-kcal-delta"
                >
                  {formatDelta(weekly_kcal_delta, " kcal")}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-zinc-900/60 border border-zinc-800">
                <div className="flex items-center gap-2">
                  {carbsUp ? <TrendingUp className="w-4 h-4 text-emerald-400" /> : <TrendingDown className="w-4 h-4 text-amber-400" />}
                  <span className="text-sm font-semibold text-zinc-100">Carbs</span>
                </div>
                <span
                  className={`text-lg font-black tabular-nums ${carbsUp ? "text-emerald-400" : "text-amber-400"}`}
                  data-testid="impact-carbs-delta"
                >
                  {formatDelta(weekly_carbs_delta_g, "g")}
                </span>
              </div>
              {Math.abs(Math.round(weekly_protein_delta_g)) > 0 && (
                <div className="flex items-center justify-between p-3 rounded-xl bg-zinc-900/60 border border-zinc-800">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-zinc-100">Proteína</span>
                  </div>
                  <span className="text-lg font-black tabular-nums text-zinc-300">
                    {formatDelta(weekly_protein_delta_g, "g")}
                  </span>
                </div>
              )}
            </div>

            {/* Spirit note */}
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/25 mb-5">
              <p className="text-[11px] text-emerald-300 leading-relaxed">
                💚 <span className="font-bold">Spirit avisou-te</span> — este plano já reflete os teus novos dias.
                Os carbs sobem pós-treino, mantêm-se baixos nos dias de descanso.
              </p>
            </div>

            <Button
              onClick={onClose}
              className="w-full h-12 text-base font-bold text-black"
              style={{
                background: "linear-gradient(135deg, #84cc16, #10b981)",
                boxShadow: "0 8px 28px rgba(132,204,22,0.5)",
              }}
              data-testid="schedule-impact-confirm"
            >
              Entendi
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
